/**
 * Rollen einer Abteilung: vorhandene Rollen zuweisen (Mehrfachauswahl).
 * Rollen werden NUR in Rollen & Rechte angelegt, hier gibt es kein Anlegen.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, MultiSelect } from '../../../components/ui';
import { departmentsApi, type DepartmentDetail, type RbacRole } from '../../../lib/departments';
import { BRAND, getThemeColors } from '../../../lib/theme';
import { apiErrorMessage } from './shared';

interface Props {
  dark: boolean;
  dept: DepartmentDetail;
  roles: RbacRole[];
  onSaved: () => void | Promise<void>;
  onOpenRoles?: () => void;
}

const CHIP_LIMIT = 5;

export function DepartmentRolesTab({ dark, dept, roles, onSaved, onOpenRoles }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [roleIds, setRoleIds] = useState<number[]>(dept.role_ids);
  // Anzeigenamen der Rechte (wie in Rollen & Rechte); ohne Label gilt der Key.
  const [labels, setLabels] = useState<Record<string, string>>({});
  useEffect(() => {
    let alive = true;
    departmentsApi.permissions()
      .then(list => { if (alive) setLabels(Object.fromEntries(list.filter(p => p.label).map(p => [p.key, p.label]))); })
      .catch(() => { /* Keys als Fallback */ });
    return () => { alive = false; };
  }, []);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState<{ kind: 'idle' | 'ok' | 'err'; msg?: string }>({ kind: 'idle' });

  const save = async () => {
    setSaving(true);
    setState({ kind: 'idle' });
    try {
      await departmentsApi.setRoles(dept.id, roleIds);
      setState({ kind: 'ok' });
      await onSaved();
    } catch (e) {
      setState({ kind: 'err', msg: apiErrorMessage(e, tr('departments.roles.save_error')) });
    } finally {
      setSaving(false);
    }
  };

  const assigned = roleIds.map(id => roles.find(r => r.id === id)).filter((r): r is RbacRole => !!r);

  return (
    <Card dark={dark} style={{ padding: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: t.heading, marginBottom: 4 }}>{tr('departments.roles.title')}</div>
      <div style={{ fontSize: 13, color: t.muted, marginBottom: 10 }}>
        {tr('departments.roles.info', { name: dept.name })}
      </div>
      {onOpenRoles && (
        <button
          type="button"
          onClick={onOpenRoles}
          style={{ background: 'none', border: 'none', color: BRAND, cursor: 'pointer', fontSize: 12, padding: 0, marginBottom: 14, fontFamily: 'Outfit, sans-serif' }}
        >
          {tr('departments.roles.manage_link')}
        </button>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {assigned.length === 0 && <div style={{ fontSize: 13, color: t.muted }}>{tr('departments.roles.none')}</div>}
        {assigned.map(r => (
          <div key={r.id} data-testid={`role-card-${r.id}`} style={{ border: `1px solid ${t.border}`, borderRadius: 12, padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: t.heading }}>{r.name}</span>
              <span style={{ fontSize: 12, color: t.muted }}>
                {tr('departments.roles.applies_to', { count: dept.members.length })}
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {r.permissions.length === 0 && <span style={{ fontSize: 12, color: t.muted }}>{tr('departments.roles.no_permissions')}</span>}
              {r.permissions.slice(0, CHIP_LIMIT).map(p => <Badge key={p} dark={dark}>{labels[p] ?? p}</Badge>)}
              {r.permissions.length > CHIP_LIMIT && <Badge dark={dark} tone="info">+{r.permissions.length - CHIP_LIMIT}</Badge>}
            </div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: t.heading, marginBottom: 6 }}>{tr('departments.roles.assign')}</div>
      <MultiSelect
        dark={dark}
        options={roles.map(r => ({ id: r.id, label: r.name }))}
        value={roleIds}
        onChange={setRoleIds}
        placeholder={tr('departments.fields.roles_placeholder')}
        ariaLabel={tr('departments.roles.assign')}
        removeLabel={n => tr('departments.common.remove_named', { name: n })}
      />
      <div style={{ fontSize: 12, color: t.muted, marginTop: 8 }}>{tr('departments.roles.list_hint')}</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18 }}>
        <Button dark={dark} variant="primary" disabled={saving} onClick={() => void save()}>{tr('departments.common.save')}</Button>
        {state.kind === 'ok' && <span style={{ fontSize: 12, color: '#027a48' }}>{tr('departments.common.saved')}</span>}
        {state.kind === 'err' && <span role="alert" style={{ fontSize: 12, color: '#b42318' }}>{state.msg}</span>}
      </div>
      <div style={{ fontSize: 12, color: t.muted, marginTop: 14 }}>{tr('departments.roles.combine_hint')}</div>
    </Card>
  );
}
